import { useTranslation } from 'react-i18next'

import { declineCall, endCall } from '#/shared/lib/telephony'
import type { TelephonyCall } from '#/shared/lib/telephony'
import { cn } from '#/shared/lib/utils'
import { CallHangupIcon, CallPickupIcon } from '#/shared/ui/icons'

import { CALL_PHASE } from '../model/use-active-call'
import type { CallPhase } from '../model/use-active-call'
import { useEndGuard } from '../model/use-end-guard'
import { CancelCallButton, EndCallButton } from './pill-controls'

const ACTION_BUTTON =
  'flex size-8 shrink-0 items-center justify-center rounded-full text-paper transition'
const INTERACTIVE = `${ACTION_BUTTON} cursor-pointer active:scale-90`
const DISABLED = `${ACTION_BUTTON} cursor-not-allowed opacity-50`

// Both accept and decline stay on screen the whole time — they just go disabled
// the moment the agent accepts (the `connecting` phase) and until Telnyx
// connects, so there's no clickable target to fat-finger during the gap and no
// jarring swap to the live End button. (Silence lives in the expanded body's
// controls row — see CallPillBody's IncomingControls.)
function IncomingActions({
  session,
  onAccept,
  onClose,
  disabled,
}: {
  session: TelephonyCall
  onAccept: () => void
  onClose: () => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  const style = disabled ? DISABLED : INTERACTIVE
  const decline = () => {
    onClose()
    void declineCall(session)
  }
  return (
    <span
      onClick={(e) => e.stopPropagation()}
      className="flex items-center gap-2"
    >
      <button
        type="button"
        disabled={disabled}
        aria-label={t('massAction.call.decline')}
        onClick={decline}
        className={cn(style, 'bg-red-400', !disabled && 'hover:bg-red-400/90')}
      >
        <CallHangupIcon className="size-4" />
      </button>
      <button
        type="button"
        disabled={disabled}
        aria-label={t('massAction.call.accept')}
        onClick={onAccept}
        className={cn(
          style,
          'w-11.5 bg-green-700',
          !disabled && 'hover:bg-green-700/90',
        )}
      >
        <CallPickupIcon className="size-4" />
      </button>
    </span>
  )
}

interface CallPillActionProps {
  phase: CallPhase
  session: TelephonyCall
  onAccept: () => void
  onClose: () => void
}

export function CallPillAction({
  phase,
  session,
  onAccept,
  onClose,
}: CallPillActionProps) {
  // A declined/abandoned incoming ring morphs this slot straight into the live
  // call's End button — hold it disabled for a beat so the tail of a decline
  // double-click can't hang up the call the agent is on (see useEndGuard).
  const endGuarded = useEndGuard(phase, session.callId)
  // Hang up without collapsing: the dropdown stays open into the `ended` phase
  // so the agent can still act on the matched orders. The shell dismisses the
  // pill once it's actually closed (outside-click / Escape).
  const endCallNow = () => {
    void endCall(session)
  }
  if (phase === CALL_PHASE.incoming || phase === CALL_PHASE.connecting) {
    return (
      <IncomingActions
        session={session}
        onAccept={onAccept}
        onClose={onClose}
        disabled={phase === CALL_PHASE.connecting}
      />
    )
  }
  if (phase === CALL_PHASE.onCall) {
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <EndCallButton onClick={endCallNow} disabled={endGuarded} />
      </span>
    )
  }
  if (phase === CALL_PHASE.calling) {
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <CancelCallButton onClick={endCallNow} disabled={endGuarded} />
      </span>
    )
  }
  if (phase === CALL_PHASE.ended) {
    // Call's over — keep the End button in place so the pill doesn't reflow,
    // just disable it.
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <EndCallButton onClick={endCallNow} disabled />
      </span>
    )
  }
  return null
}
